'use client';

import { FavoriteActiveIcon } from '@/components/icons/favorite-active-icon';
import { RichTextRenderer } from '@/components/rich-text/rich-text-renderer';
import { Anchor } from '@/components/ui/anchor';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { formatCurrency } from '@/utils/currency';
import { MovieRewardsInfo } from '@cinemark/content-api-client';
import { clsx } from 'clsx';
import Image from 'next/image';

type BenefitsProps = {
  movieRewardsInfo: MovieRewardsInfo;
  showMobileLinks?: boolean;
};

export const Benefits = ({ movieRewardsInfo, showMobileLinks }: BenefitsProps): React.JSX.Element => {
  const { trackClick } = useDataAnalytics();
  const tableRowClasses = clsx('max-md:flex max-md:flex-wrap max-md:[&>*:first-child]:w-full',
    'max-md:[&>*:nth-last-child(-n+2)]:w-1/2 max-md:[&>*:nth-child(2)]:border-r-0 max-md:text-center');

  return (
    <Table addTopBottomBorder
      className="w-full table-fixed [&_.payload-richtext_*]:text-sm md:[&_.payload-richtext_*]:text-base md:text-base"
    >
      <TableHeader>
        <TableRow className={tableRowClasses}>
          <TableHead className="w-full md:w-2/3 bg-primary flex items-center md:table-cell"
            data-test="table-header-movie-rewards"
          >
            <picture>
              <source srcSet="/img/movie-rewards/logo_movie_rewards_w_2025.png 1x, /img/movie-rewards/logo_movie_rewards_w_2025@2x.png 2x"/>
              <Image alt="Cinemark Movie Rewards Movie Rewards"
                className="w-auto h-6"
                height={30}
                src="/img/movie-rewards/logo_movie_rewards_w_2025.png"
                width={105}
              />
            </picture>
          </TableHead>

          <TableHead className="w-1/2 md:w-1/6 bg-[var(--color-movie-fan-info)] flex justify-center items-center md:table-cell"
            data-test="table-header-movie-fan"
          >
            <picture>
              <source srcSet="/img/movie-rewards/logo_mf_w_2025.png 1x, /img/movie-rewards/logo_mf_w_2025@2x.png 2x"/>
              <Image alt="Cinemark Movie Rewards Movie Fan"
                className="max-w-full mx-auto"
                height={30}
                src="/img/movie-rewards/logo_mf_w_2025.png"
                width={105}
              />
            </picture>
          </TableHead>

          <TableHead className="w-1/2 md:w-1/6 bg-[var(--color-movie-club-info)] flex justify-center items-center md:table-cell"
            data-test="table-header-movie-club"
          >
            <picture>
              <source srcSet="/img/movie-rewards/logo_mc_w_2025.png 1x, /img/movie-rewards/logo_mc_w_2025@2x.png 2x"/>
              <Image alt="Cinemark Movie Rewards Movie Club"
                className="max-w-full mx-auto"
                height={30}
                src="/img/movie-rewards/logo_mc_w_2025.png"
                width={105}
              />
            </picture>
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow addColumnBorder
          className={tableRowClasses}
        >
          <TableCell data-test="table-row-price-per-month">
            Price Per Month
          </TableCell>
          <TableCell className="text-center font-bold"
            data-test="table-row-price-per-month-movie-fan"
          >
            FREE!
          </TableCell>
          <TableCell className="text-center font-bold"
            data-test="table-row-price-per-month-movie-club"
          >
            Starting at {formatCurrency(movieRewardsInfo.movieRewardsStartingPrice ? +movieRewardsInfo.movieRewardsStartingPrice : 10.99)}
          </TableCell>
        </TableRow>

        {movieRewardsInfo.memberBenefits?.map((benefit, index) =>
          <TableRow key={benefit.id}
            addColumnBorder
            className={tableRowClasses}
          >
            <TableCell className="w-full md:w-2/3"
              data-test={`table-row-benefit-${index}`}
            >
              <RichTextRenderer className="text-wrap"
                content={benefit.memberBenefitsText}
              />
            </TableCell>
            <TableCell className="w-1/2 md:w-1/6 "
              data-test={`table-row-benefit-movie-fan-${index}`}
            >
              {benefit.checkedForMovieFans &&
                <FavoriteActiveIcon className="text-[var(--color-movie-fan-info)] icon mx-auto" />
              }

              {!benefit.checkedForMovieFans &&
                <>
                  <label className="hidden sr-only">Not a Movie Fan benefit</label>
                  <span className="text-transparent">x</span>
                </>
              }
            </TableCell>
            <TableCell className="w-1/2 md:w-1/6"
              data-test={`table-row-benefit-movie-club-${index}`}
            >
              {benefit.checkedForMovieClub &&
                <FavoriteActiveIcon className="text-[var(--color-movie-club-info)] icon mx-auto" />
              }

              {!benefit.checkedForMovieClub &&
                <>
                  <label className="hidden sr-only">Not a Movie Club benefit</label>
                  <span className="text-transparent">x</span>
                </>
              }
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      <TableFooter>
        <TableRow addColumnBorder
          className={tableRowClasses}
        >
          <TableCell className="w-full md:w-2/3 max-md:order-last">
            <RichTextRenderer className="font-normal text-on-surface-subtle-darker text-wrap text-left max-md:[&_*]:text-xs"
              content={movieRewardsInfo.benefitsTableDisclaimerText}
              data-test="table-row-disclaimer"
            />
          </TableCell>
          <TableCell className="flex flex-col justify-between w-1/2 md:w-1/6 md:table-cell">
            <h3 className="text-sm text-center mb-2 text-wrap md:hidden"
              data-test="mobile-heading-movie-fan"
            >
              Become a Movie Fan for Free
            </h3>
            <Anchor className="w-full"
              data-test="link-footer-join-now"
              href={showMobileLinks ? '/membership/signin?returnurl=/subscriptions' : '/membership/register?returnurl=/movie-rewards-info'}
              size="lg"
              onClick={() => trackClick({
                linkCategory: AnalyticsPageCategories.MOVIE_REWARDS,
                linkName: `Movie Fan ${showMobileLinks ? 'Mobile' : 'Desktop'} Footer CTA`,
              })}
            >
              {movieRewardsInfo.benefitsTableSignedOutMovieFanButtonCTA}
            </Anchor>
          </TableCell>
          <TableCell className="flex flex-col justify-between w-1/2 md:w-1/6 md:table-cell">
            <h3 className="text-sm text-center mb-2 text-wrap md:hidden"
              data-test="mobile-heading-movie-club"
            >
              Upgrade to Movie Club and Get More
            </h3>
            <Anchor className="w-full"
              data-test="link-footer-upgrade"
              href={showMobileLinks ? '/subscriptions/mobile?mode=SignUp&scancc=1' : '/movieclub'}
              size="lg"
              onClick={() => trackClick({
                linkCategory: AnalyticsPageCategories.MOVIE_REWARDS,
                linkName: `Movie Club ${showMobileLinks ? 'Mobile' : 'Desktop'} Footer CTA`,
              })}
            >
              {movieRewardsInfo.benefitsTableSignedOutMovieClubButtonCTA}
            </Anchor>
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
};
